import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { PageHeader, Card, StatusPill } from '@/components/Primitives';
import { TID } from '@/constants/testIds/voice';
import { Plus, PencilSimple, Trash } from '@phosphor-icons/react';
import { Modal, Field } from './Extensions';
import { useToast } from '@/hooks/use-toast';

const RingGroups = () => {
    const [items, setItems] = useState([]);
    const [exts, setExts] = useState([]);
    const [editing, setEditing] = useState(null);
    const { toast } = useToast();

    const load = async () => {
        const [r, e] = await Promise.all([api.get('/ring-groups'), api.get('/extensions')]);
        setItems(r.data); setExts(e.data);
    };
    useEffect(() => { load(); }, []);

    const save = async (e) => {
        e.preventDefault();
        try {
            if (editing.id) {
                await api.patch(`/ring-groups/${editing.id}`, editing);
            } else {
                await api.post('/ring-groups', editing);
            }
            setEditing(null);
            load();
            toast({ title: 'Uloženo' });
        } catch (err) {
            toast({ title: 'Chyba', description: err.response?.data?.detail || 'error', variant: 'destructive' });
        }
    };
    const del = async (id) => {
        if (!window.confirm('Smazat ring group?')) return;
        await api.delete(`/ring-groups/${id}`); load();
    };

    return (
        <>
            <PageHeader
                eyebrow="Routing"
                title="Ring Groups"
                description="Skupina klapek, které zvoní současně, sekvenčně nebo round-robin."
                actions={
                    <button data-testid={TID.rgCreateBtn}
                            onClick={() => setEditing({ name: '', extension_ids: [], strategy: 'all_at_once', timeout_seconds: 25, fallback_destination: '', is_active: true })}
                            className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-[0.08em]"
                            style={{ background: 'var(--brand)', color: '#fff', borderRadius: 2 }}>
                        <Plus size={14} weight="bold" />Nová skupina
                    </button>
                }
            />

            <Card testId={TID.rgTable}>
                {items.length === 0 ? (
                    <div className="py-8 text-center text-sm font-mono" style={{ color: 'var(--text-secondary)' }}>
                        Žádné skupiny.
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-[0.1em]"
                                style={{ color: 'var(--text-secondary)' }}>
                                <th className="text-left py-2">Název</th>
                                <th className="text-left">Členové</th>
                                <th className="text-left">Strategie</th>
                                <th className="text-center">Timeout</th>
                                <th className="text-center">Stav</th>
                                <th className="text-right">Akce</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((g) => (
                                <tr key={g.id} className="border-t"
                                    style={{ borderColor: 'var(--border-default)' }}>
                                    <td className="py-2">{g.name}</td>
                                    <td className="font-mono text-xs">
                                        {(g.extension_ids || []).map((id) => exts.find((e) => e.id === id)?.extension).filter(Boolean).join(', ') || '—'}
                                    </td>
                                    <td className="font-mono text-xs">{g.strategy}</td>
                                    <td className="text-center font-mono">{g.timeout_seconds}s</td>
                                    <td className="text-center">
                                        <StatusPill status={g.is_active ? 'success' : 'neutral'}
                                                    label={g.is_active ? 'aktivní' : 'pozastaveno'} />
                                    </td>
                                    <td className="text-right">
                                        <button onClick={() => setEditing({ ...g })} className="p-1 hover:bg-[var(--bg-surface-hover)]"
                                                data-testid={`rg-edit-${g.id}`}><PencilSimple size={14} /></button>
                                        <button onClick={() => del(g.id)} className="p-1 hover:bg-[var(--bg-surface-hover)] ml-1"
                                                data-testid={`rg-delete-${g.id}`}><Trash size={14} /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Card>

            {editing && (
                <Modal title={editing.id ? 'Editace skupiny' : 'Nová ring group'} onClose={() => setEditing(null)}>
                    <form onSubmit={save} className="space-y-3">
                        <Field label="Název" value={editing.name}
                               onChange={(v) => setEditing({ ...editing, name: v })} required />
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.1em] font-bold"
                                   style={{ color: 'var(--text-secondary)' }}>Členové (klapky)</label>
                            <div className="mt-1 grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 border"
                                 style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                                {exts.map((e) => (
                                    <label key={e.id} className="inline-flex items-center gap-2 text-sm">
                                        <input type="checkbox"
                                               checked={(editing.extension_ids || []).includes(e.id)}
                                               onChange={(ev) => {
                                                   const cur = editing.extension_ids || [];
                                                   const next = ev.target.checked ? [...cur, e.id] : cur.filter((x) => x !== e.id);
                                                   setEditing({ ...editing, extension_ids: next });
                                               }} />
                                        <span className="font-mono text-xs">{e.extension}</span>
                                        <span className="text-xs">{e.display_name}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <div>
                                <label className="text-[10px] uppercase tracking-[0.1em] font-bold"
                                       style={{ color: 'var(--text-secondary)' }}>Strategie</label>
                                <select value={editing.strategy}
                                        onChange={(e) => setEditing({ ...editing, strategy: e.target.value })}
                                        className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-app)] border text-sm"
                                        style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                                    <option value="all_at_once">All at once</option>
                                    <option value="sequential">Sequential</option>
                                    <option value="round_robin">Round robin</option>
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-[0.1em] font-bold"
                                       style={{ color: 'var(--text-secondary)' }}>Timeout (s)</label>
                                <input type="number" value={editing.timeout_seconds}
                                       onChange={(e) => setEditing({ ...editing, timeout_seconds: parseInt(e.target.value, 10) })}
                                       className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-app)] border text-sm font-mono"
                                       style={{ borderColor: 'var(--border-default)', borderRadius: 2 }} />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-[0.1em] font-bold"
                                       style={{ color: 'var(--text-secondary)' }}>Fallback</label>
                                <input value={editing.fallback_destination || ''}
                                       onChange={(e) => setEditing({ ...editing, fallback_destination: e.target.value })}
                                       className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-app)] border text-sm font-mono"
                                       style={{ borderColor: 'var(--border-default)', borderRadius: 2 }} />
                            </div>
                        </div>
                        <div className="flex justify-end gap-2">
                            <button type="button" onClick={() => setEditing(null)}
                                    className="px-3 py-2 text-xs font-bold uppercase border"
                                    style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>Zrušit</button>
                            <button type="submit" data-testid={TID.rgSaveBtn}
                                    className="px-3 py-2 text-xs font-bold uppercase"
                                    style={{ background: 'var(--brand)', color: '#fff', borderRadius: 2 }}>Uložit</button>
                        </div>
                    </form>
                </Modal>
            )}
        </>
    );
};

export default RingGroups;
